/* BP-017 — Acquisition Manager Scorecard (Workspace Leaderboard).
   Team-wide performance dashboard. Neutral coaching aesthetic — no "shame" styling. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Download, TrendingUp, Trophy, Target } from "lucide-react";


const BAND_TW = {
  elite:              { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", bar: "bg-emerald-500" },
  strong:             { bg: "bg-sky-50",     text: "text-sky-700",     ring: "ring-sky-200",     bar: "bg-sky-500" },
  developing:         { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200",   bar: "bg-amber-500" },
  needs_coaching:     { bg: "bg-orange-50",  text: "text-orange-700",  ring: "ring-orange-200",  bar: "bg-orange-500" },
  critical_attention: { bg: "bg-red-50",     text: "text-red-700",     ring: "ring-red-200",     bar: "bg-red-500" },
};


export default function AcquisitionScorecardPage() {
  const [items, setItems]   = useState([]);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/acquisition/scorecards");
      setItems(data.items || []);
      setPeriod(data.period);
    } catch (err) {
      toast.error("Failed to load scorecards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recalculate = useCallback(async () => {
    setRecalculating(true);
    try {
      const { data } = await api.post(
        "/v3/acquisition/scorecards/recalculate", { reason: "manual" });
      toast.success(`Recalculated ${data.count} scorecards`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recalculation failed");
    } finally {
      setRecalculating(false);
    }
  }, [load]);

  const exportPdf = useCallback(() => {
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/acquisition/exports/pdf`,
      "_blank");
  }, []);
  const exportCsv = useCallback(() => {
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/acquisition/exports/csv`,
      "_blank");
  }, []);

  const summary = useMemo(() => {
    if (!items.length) return null;
    const totalRev = items.reduce(
      (s, it) => s + (it.metrics?.gross_assignment_revenue || 0), 0);
    const totalContracts = items.reduce(
      (s, it) => s + (it.metrics?.contracts_signed || 0), 0);
    const totalNew = items.reduce(
      (s, it) => s + (it.metrics?.new_leads_assigned || 0), 0);
    const avgScore = items.reduce((s, it) => s + it.overall_score, 0) / items.length;
    return {
      totalRev, totalContracts, totalNew,
      avgScore: Math.round(avgScore * 10) / 10,
    };
  }, [items]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-600 text-sm"
             data-testid="acq-loading">
        <Loader2 size={14} className="animate-spin"/> Loading scorecards…
      </div>);
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4"
          data-testid="acquisition-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
            <Trophy size={12}/> BP-017 · Acquisition Manager Scorecard
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Team Performance</h1>
          {period && (
            <div className="text-xs text-slate-500 mt-0.5">
              {period.start} → {period.end} · {period.type}
            </div>)}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={recalculate} disabled={recalculating}
                  data-testid="acq-recalc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {recalculating ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
            Recalculate All
          </button>
          <button onClick={exportPdf} data-testid="acq-pdf"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400">
            <Download size={12}/> PDF
          </button>
          <button onClick={exportCsv} data-testid="acq-csv"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400">
            <Download size={12}/> CSV
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="acq-summary">
          <SummaryTile label="Team Avg Score" value={summary.avgScore.toFixed(1)} suffix="/ 100"/>
          <SummaryTile label="Total Contracts" value={summary.totalContracts}/>
          <SummaryTile label="Total Revenue" value={`$${summary.totalRev.toLocaleString()}`}/>
          <SummaryTile label="New Leads" value={summary.totalNew}/>
        </div>
      )}

      {/* Leaderboard */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
            data-testid="acq-leaderboard">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Trophy size={14} className="text-slate-500"/>
          <span className="text-sm font-semibold text-slate-800">Leaderboard</span>
          <span className="text-xs text-slate-500 ml-auto">{items.length} managers</span>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No scorecards yet. Click <b>Recalculate All</b> to generate one for every manager.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2">Rank</th>
                  <th className="text-left px-4 py-2">Manager</th>
                  <th className="text-right px-4 py-2">Score</th>
                  <th className="text-left px-4 py-2">Band</th>
                  <th className="text-right px-4 py-2">New Leads</th>
                  <th className="text-right px-4 py-2">Contracts</th>
                  <th className="text-right px-4 py-2">Revenue</th>
                  <th className="text-right px-4 py-2">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const band = BAND_TW[it.band] || BAND_TW.developing;
                  return (
                    <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50"
                         data-testid={`acq-row-${it.manager_id}`}>
                      <td className="px-4 py-2 text-slate-500 tabular-nums">#{i + 1}</td>
                      <td className="px-4 py-2">
                        <Link to={`/v3/acquisition/managers/${it.manager_id}`}
                               className="font-medium text-slate-900 hover:underline"
                               data-testid={`acq-mgr-link-${it.manager_id}`}>
                          {it.manager_name}
                        </Link>
                        {it.manager_email && (
                          <div className="text-[10px] text-slate-500">{it.manager_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">
                        {it.overall_score.toFixed(1)}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${band.bg} ${band.text} ring-1 ${band.ring}`}>
                          <span className={`inline-block h-1.5 w-1.5 rounded-full ${band.bar}`}/>
                          {it.band_label}
                          {it.provisional && (
                            <span className="ml-1 opacity-70 uppercase text-[9px]">provisional</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        {it.metrics?.new_leads_assigned || 0}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        {it.metrics?.contracts_signed || 0}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        ${(it.metrics?.gross_assignment_revenue || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                        {(it.confidence * 100).toFixed(0)}%
                      </td>
                    </tr>);
                })}
              </tbody>
            </table>
          </div>)}
      </div>

      {/* Disclaimer */}
      {items.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500"
              data-testid="acq-disclaimer">
          <div className="flex items-start gap-2">
            <Target size={12} className="mt-0.5 flex-shrink-0"/>
            <span><i>{items[0].disclaimer}</i></span>
          </div>
        </div>
      )}
    </div>);
}


function SummaryTile({ label, value, suffix }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <div className="text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
        {suffix && <div className="text-xs text-slate-500">{suffix}</div>}
      </div>
    </div>);
}
